import { useEffect, useReducer } from 'react';
import { AnchorAPI } from '../modules/AnchorAPI';
import { FeatureFlag } from '../modules/FeatureFlags';
import { localStorage } from '../modules/Browser/localStorage';

const INITIALIZED = 'INITIALIZED';
const FETCH = 'FETCH';
const FETCH_SUCCESS = 'FETCH_SUCCESS';
const FETCH_FAIL = 'FETCH_FAIL';
const FEATURE_FLAG_KEY = 'featureFlags';
enum STATUS {
  IDLE,
  LOADING,
  SUCCESS,
  FAIL,
}

type State = {
  error: string | null;
  status: STATUS;
  featureFlags?: FeatureFlags | null;
  isInitializedFromBrowserStorage: boolean;
};
type Action =
  | {
      type: typeof INITIALIZED;
      payload: { featureFlags: FeatureFlags };
    }
  | {
      type: typeof FETCH;
    }
  | { type: typeof FETCH_SUCCESS; payload: { featureFlags: FeatureFlags } }
  | { type: typeof FETCH_FAIL; payload: { error: string } };

type FeatureFlags = FeatureFlag[];

function reducer(state: State, action: Action) {
  switch (action.type) {
    case INITIALIZED:
      return {
        ...state,
        featureFlags: action.payload.featureFlags,
        isInitializedFromBrowserStorage: true,
      };
    case FETCH:
      return { ...state, status: STATUS.LOADING, error: null };
    case FETCH_SUCCESS:
      return {
        ...state,
        status: STATUS.SUCCESS,
        featureFlags: action.payload.featureFlags,
      };
    case FETCH_FAIL:
      return { ...state, status: STATUS.FAIL, error: action.payload.error };
    default:
      return state;
  }
}

const initialState: State = {
  error: null,
  status: STATUS.IDLE,
  featureFlags: undefined,
  isInitializedFromBrowserStorage: false,
};

function useFeatureFlags() {
  const [state, dispatch] = useReducer(reducer, initialState);

  /** fetch and set feature flags in browser storage and state */
  async function handleSetFeatureFlags() {
    try {
      dispatch({ type: FETCH });
      const {
        error: newError,
        featureFlags: newFeatureFlags,
      } = await setFeatureFlagsIndexedDb();
      if (newFeatureFlags) {
        dispatch({
          type: FETCH_SUCCESS,
          payload: { featureFlags: newFeatureFlags },
        });
      }
      if (newError) {
        dispatch({
          type: FETCH_FAIL,
          payload: { error: newError },
        });
      }
    } catch (err) {
      dispatch({
        type: FETCH_FAIL,
        payload: { error: err.message },
      });
    }
  }

  /**
   * on page load, see if there are feature flags stored in browser storage
   * and set them into state
   */
  useEffect(() => {
    localStorage.getItem(FEATURE_FLAG_KEY).then(featureFlagsStored => {
      dispatch({
        type: INITIALIZED,
        payload: { featureFlags: featureFlagsStored || null },
      });
    });
  }, []);
  return { handleSetFeatureFlags, state };
}

/**
 * Sets feature flags in browser storage. If you pass an argument, it will set
 * that value as the feature flag. Otherwise, it will fetch the current user's
 * feature flags and sets them in browser storage
 * @param newFeatureFlags
 */
async function setFeatureFlagsIndexedDb(newFeatureFlags?: FeatureFlags | null) {
  try {
    let featureFlags = newFeatureFlags;
    let error;
    if (featureFlags === undefined) {
      const {
        json,
        error: fetchError,
      } = await AnchorAPI.fetchAllEnabledFeatureFlags();
      if (json) {
        featureFlags = json.allEnabledFlags.map(({ name }) => name);
      }
      error = fetchError;
    }
    localStorage.setItem(FEATURE_FLAG_KEY, featureFlags);
    return { error, featureFlags };
  } catch (err) {
    throw new Error(err.message);
  }
}

export { useFeatureFlags, setFeatureFlagsIndexedDb, FEATURE_FLAG_KEY, STATUS };
